/**
 * Settings handling shared by server and client:
 *  - `coerceSettings`: config settings (legacy strings like "1", "true", "a,b") → typed values
 *    according to the element's `settingsSchema`, including legacy aliases (`x-legacy`);
 *  - `settingsFields`: derive the admin form for a settings schema, rendered with the same
 *    element addons as data forms (`x-ui.element`).
 */
import type { SettingsSchema } from './manifest.js';
import { err, type ElementError, type ElementOption } from './types.js';

type Prop = Record<string, unknown>;

export interface CoercedSettings<S = Record<string, unknown>> {
  settings: S;
  /** settings that could not be coerced (dropped, default applies) */
  issues: (ElementError & { key: string })[];
  /** keys not declared in the schema (ignored) */
  unknown: string[];
}

const FALSE = /^(0|false|no|off)$/i;

/**
 * Legacy flags are "set" when present (`defined $SETTING{readonly}`), so an empty string is true;
 * "0", "false", "no", "off" are false.
 */
export function legacyBool(v: unknown): boolean | undefined {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'string') return !FALSE.test(v.trim());
  return undefined;
}

function coerceValue(prop: Prop, raw: unknown): { ok: true; value: unknown } | { ok: false } {
  const type = prop.type;
  let value: unknown = raw;
  switch (type) {
    case 'boolean': {
      const b = legacyBool(raw);
      if (b === undefined) return { ok: false };
      value = b;
      break;
    }
    case 'integer':
    case 'number': {
      const n = typeof raw === 'number' ? raw : typeof raw === 'string' && raw.trim() !== '' ? Number(raw.trim()) : NaN;
      if (!Number.isFinite(n) || (type === 'integer' && !Number.isInteger(n))) return { ok: false };
      if (typeof prop.minimum === 'number' && n < prop.minimum) return { ok: false };
      if (typeof prop.maximum === 'number' && n > prop.maximum) return { ok: false };
      value = n;
      break;
    }
    case 'string':
      if (typeof raw === 'number' || typeof raw === 'boolean') value = String(raw);
      else if (typeof raw !== 'string') return { ok: false };
      break;
    case 'array': {
      const items = (prop.items ?? {}) as Prop;
      const list = Array.isArray(raw)
        ? raw
        : typeof raw === 'string'
          ? raw.split(',').map((s) => s.trim()).filter((s) => s !== '')
          : undefined;
      if (!list) return { ok: false };
      const out: unknown[] = [];
      for (const x of list) {
        const c = coerceValue(items.type ? items : { ...items, type: 'string' }, x);
        if (!c.ok) return { ok: false };
        out.push(c.value);
      }
      value = out;
      break;
    }
    case 'object':
      if (typeof raw === 'string') {
        try {
          value = JSON.parse(raw);
        } catch {
          return { ok: false };
        }
      }
      if (!value || typeof value !== 'object' || Array.isArray(value)) return { ok: false };
      break;
    default:
      return { ok: false };
  }
  if (Array.isArray(prop.enum) && !prop.enum.includes(value)) return { ok: false };
  return { ok: true, value };
}

/** Coerce raw config settings to typed settings (defaults applied, unknown keys reported). */
export function coerceSettings<S = Record<string, unknown>>(
  schema: SettingsSchema,
  raw: Readonly<Record<string, unknown>> | undefined,
  implied?: Readonly<Record<string, unknown>>,
): CoercedSettings<S> {
  const props = schema.properties as Record<string, Prop>;
  const alias = new Map<string, string>();
  for (const [key, p] of Object.entries(props)) {
    for (const a of (p['x-legacy'] as string[] | undefined) ?? []) alias.set(a, key);
  }
  const out: Record<string, unknown> = {};
  const issues: CoercedSettings['issues'] = [];
  const unknown: string[] = [];
  const explicit = new Set<string>();
  for (const [layer, source] of [['implied', implied], ['config', raw]] as const) {
    for (const [rawKey, v] of Object.entries(source ?? {})) {
      const key = rawKey in props ? rawKey : alias.get(rawKey) ?? alias.get(rawKey.replace(/\$$/, ''));
      if (!key) {
        if (layer === 'config') unknown.push(rawKey);
        continue;
      }
      // a canonical key wins over its legacy alias regardless of order
      if (layer === 'config' && explicit.has(key) && rawKey !== key) continue;
      const c = coerceValue(props[key] as Prop, v);
      if (!c.ok) {
        if (layer === 'config') issues.push({ ...err('element.invalid_setting', { setting: rawKey }), key: rawKey });
        continue;
      }
      out[key] = c.value;
      if (layer === 'config' && rawKey === key) explicit.add(key);
    }
  }
  for (const [key, p] of Object.entries(props)) {
    if (!(key in out) && 'default' in p) out[key] = structuredClone(p.default);
  }
  return { settings: out as S, issues, unknown };
}

/** One field of a generated settings form. */
export interface SettingsField {
  key: string;
  /** element id used to edit the setting */
  element: string;
  label: string;
  help?: string;
  group?: string;
  settings: Record<string, unknown>;
  options?: ElementOption[];
  default?: unknown;
}

/**
 * Derive the admin settings form for an element: every property becomes a field rendered by an
 * element addon (`x-ui.element`, else by type: boolean → checkbox, enum → select,
 * integer/number → number, array with enum items → checkbox-ng, other → text).
 */
export function settingsFields(schema: SettingsSchema): SettingsField[] {
  const fields = Object.entries(schema.properties as Record<string, Prop>).map(([key, p], i) => {
    const ui = (p['x-ui'] ?? {}) as { element?: string; order?: number; group?: string; help?: string; settings?: Record<string, unknown> };
    const items = (p.items ?? {}) as Prop;
    const enumValues = (p.enum ?? items.enum) as unknown[] | undefined;
    const element =
      ui.element ??
      (p.type === 'boolean'
        ? 'checkbox'
        : p.type === 'array' && enumValues
          ? 'checkbox-ng'
          : enumValues
            ? 'select'
            : p.type === 'integer' || p.type === 'number'
              ? 'number'
              : p.type === 'object'
                ? 'textarea'
                : 'text');
    const settings: Record<string, unknown> = { ...(ui.settings ?? {}) };
    if (element === 'number') {
      if (typeof p.minimum === 'number') settings.min ??= p.minimum;
      if (typeof p.maximum === 'number') settings.max ??= p.maximum;
      if (p.type === 'integer') settings.step ??= 1;
    }
    const field: SettingsField & { order: number } = {
      key,
      element,
      label: typeof p.title === 'string' ? p.title : key,
      settings,
      order: ui.order ?? 1000 + i,
    };
    if (typeof p.description === 'string' || ui.help) field.help = ui.help ?? (p.description as string);
    if (ui.group) field.group = ui.group;
    if (enumValues) field.options = enumValues.map((v) => ({ value: String(v), label: String(v) }));
    if ('default' in p) field.default = p.default;
    return field;
  });
  return fields.sort((a, b) => a.order - b.order).map(({ order: _o, ...f }) => f);
}
